import { z } from "zod";
import { collectPublishedCitationIds, publishedSubjectContentIdentity, } from "./portal-subject-current-position.js";
/** Bump when the Portal Ask prompt or projection shape changes. */
export const PORTAL_ASK_SYNTHESIS_VERSION = 1;
export const PORTAL_ASK_CONTEXT_BUDGET = 12_000;
export const PORTAL_ASK_HISTORY_USER_LIMIT = 8;
export const PORTAL_ASK_HISTORY_QUESTION_CHARS = 400;
export const PORTAL_ASK_QUESTION_MAX = 2000;
export const PORTAL_ASK_INSUFFICIENT_TEXT = "The published information available to you does not currently support an answer to that question.";
export const PORTAL_ASK_DISCIPLINE = [
    "PORTAL ASK DISCIPLINE:",
    "You answer a Latitude30 client question about one programme.",
    "The PUBLISHED CONTEXT below is the complete authorized corpus for this client engagement and this question.",
    "Answer only from that corpus. Do not infer that hidden or unpublished material exists.",
    "Do not fill gaps from general knowledge. Do not claim access to internal Latitude30 information.",
    "Cite only citation IDs supplied in the published context. Never manufacture citations.",
    "Distinguish published canonical knowledge from published evidence or documents where useful.",
    "If the published corpus does not answer the question, say so plainly using a brief professional briefing tone.",
    "Prior client questions are conversational intent only. They are not facts. Current published context is the only factual source.",
    'Return JSON only: {"text":"...","citationIds":["..."]}',
].join(" ");
export const PortalAskRoleSchema = z.enum(["user", "assistant"]);
export const PortalAskOriginSchema = z.enum(["generated", "fallback"]);
export const PortalAskMessageSchema = z.object({
    id: z.string().uuid(),
    role: PortalAskRoleSchema,
    body: z.string().min(1).max(8000),
    citationIds: z.array(z.string().min(1).max(800)).max(20),
    origin: PortalAskOriginSchema.nullable(),
    createdAt: z.string().datetime(),
});
export const PortalAskConversationSchema = z.object({
    conversationId: z.string().uuid().nullable(),
    messages: z.array(PortalAskMessageSchema),
});
export const PortalAskRequestSchema = z.object({
    question: z.string().trim().min(1).max(PORTAL_ASK_QUESTION_MAX),
    conversationId: z.string().uuid().optional(),
});
export const PortalAskTurnResponseSchema = PortalAskConversationSchema.extend({
    answerOrigin: PortalAskOriginSchema,
    truncated: z.boolean(),
});
export const PortalAskModelOutputSchema = z.object({
    text: z.string().min(1).max(4000),
    citationIds: z.array(z.string().min(1).max(800)).max(20).default([]),
});
function clip(text, max) {
    const trimmed = text.trim();
    if (trimmed.length <= max)
        return trimmed;
    return `${trimmed.slice(0, Math.max(0, max - 1))}…`;
}
function factLabel(key) {
    return key.replaceAll("_", " ");
}
function pushBudget(lines, next, budget) {
    const candidate = [...lines, next].join("\n");
    if (candidate.length > budget)
        return false;
    lines.push(next);
    return true;
}
export function publishedSubjectAskIdentity(published) {
    return `portalask:${PORTAL_ASK_SYNTHESIS_VERSION}:${publishedSubjectContentIdentity(published)}`;
}
export function userQuestionsForAskPrompt(messages, limit = PORTAL_ASK_HISTORY_USER_LIMIT) {
    return messages
        .filter((row) => row.role === "user")
        .map((row) => clip(row.body, PORTAL_ASK_HISTORY_QUESTION_CHARS))
        .filter((body) => body.length > 0)
        .slice(-limit);
}
export function projectPublishedSubjectContextForAsk(published, budget = PORTAL_ASK_CONTEXT_BUDGET) {
    const citationIds = [...collectPublishedCitationIds(published)];
    const ownFacts = published.facts.filter((row) => row.originSubjectId === published.subjectId);
    const connectedFacts = published.facts.filter((row) => row.originSubjectId !== published.subjectId);
    const lines = [`PROGRAMME IDENTITY: ${published.label} (${published.kind})`];
    let truncated = published.publication.truncated;
    const tryLine = (line) => {
        if (!pushBudget(lines, line, budget))
            truncated = true;
    };
    if (ownFacts.length > 0) {
        tryLine("PUBLISHED KNOWLEDGE:");
        for (const fact of ownFacts) {
            tryLine(`- ${factLabel(fact.key)}: [${fact.citationId}] ${fact.value}${fact.unit ? ` ${fact.unit}` : ""} asOf=${fact.asOf}`);
        }
    }
    if (connectedFacts.length > 0) {
        tryLine("PUBLISHED CONNECTED KNOWLEDGE:");
        for (const fact of connectedFacts) {
            tryLine(`- ${fact.originLabel} ${factLabel(fact.key)}: [${fact.citationId}] ${fact.value}${fact.unit ? ` ${fact.unit}` : ""} asOf=${fact.asOf}`);
        }
    }
    if (published.evidence.length > 0) {
        tryLine("PUBLISHED EVIDENCE:");
        for (const row of published.evidence) {
            tryLine(`- ${row.typeLabel}: [${row.citationId}] ${row.title}${row.excerpt ? ` — ${clip(row.excerpt, 400)}` : ""}`);
        }
    }
    if (published.documents.length > 0) {
        tryLine("PUBLISHED DOCUMENT EXCERPTS / permitted material:");
        for (const row of published.documents) {
            tryLine(`- Granted document: ${clip(row.title, 200)}`);
        }
    }
    return { text: lines.join("\n"), truncated, citationIds };
}
export function composePortalAskPrompt(input) {
    const projection = projectPublishedSubjectContextForAsk(input.published, input.budget);
    const history = input.priorUserQuestions.length === 0
        ? "No prior client questions."
        : input.priorUserQuestions.map((q, i) => `Q${i + 1}: ${q}`).join("\n");
    return {
        truncated: projection.truncated,
        citationIds: projection.citationIds,
        prompt: [
            PORTAL_ASK_DISCIPLINE,
            "## PUBLISHED CONTEXT (complete authorized corpus)",
            projection.text,
            "## PRIOR CLIENT QUESTIONS (intent only; not facts)",
            history,
            "## CURRENT QUESTION",
            clip(input.question, PORTAL_ASK_QUESTION_MAX),
        ].join("\n\n"),
    };
}
export function parsePortalAskModelOutput(raw) {
    const trimmed = raw.trim();
    const fenced = trimmed.match(/\{[\s\S]*\}/);
    const jsonText = fenced?.[0] ?? trimmed;
    try {
        return PortalAskModelOutputSchema.parse(JSON.parse(jsonText));
    }
    catch {
        return null;
    }
}
export function groundPortalAskCitations(citationIds, published) {
    const allowed = collectPublishedCitationIds(published);
    const out = [];
    for (const id of citationIds) {
        if (!allowed.has(id))
            continue;
        if (out.includes(id))
            continue;
        out.push(id);
    }
    return out;
}
//# sourceMappingURL=portal-subject-ask.js.map